import { Loader } from '@fukuri-admins/ui/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { Suspense } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useGetPartnerAdminsPartnerFeatureFlags } from '~/hooks';
import { EditUserForm } from '~/features/EditUserForm';
import { EditUserFormBankAccount } from '~/features/EditUserFormBankAccount';
import { EditUserHeader } from '~/features/EditUserHeader';
import { getUsersLink } from '~/utils/getLinks';
import styles from './styles.module.scss';

type EditUserContentProps = {
  /** 利用者ID */
  userId: number;
};

/**
 * 利用者編集コンテンツ
 *
 * partner_feature_flag.bank_account の ON/OFF で表示する Feature を切り替える。
 * `useGetPartnerAdminsPartnerFeatureFlags` は suspense: true のため、
 * 親 Screen の Suspense 境界内でレンダーされる必要がある。
 */
const EditUserContent: React.FC<EditUserContentProps> = ({ userId }) => {
  const threeLetterCode = getThreeLetterCode() ?? '';
  const { data } = useGetPartnerAdminsPartnerFeatureFlags({ threeLetterCode });

  const isBankAccountEnabled = (data.partner_feature_flags ?? []).some(
    f => f.feature_key === 'bank_account' && f.enabled
  );

  return isBankAccountEnabled ? (
    <EditUserFormBankAccount userId={userId} />
  ) : (
    <EditUserForm userId={userId} />
  );
};

/**
 * 利用者編集 Screen（新パターン）
 *
 * - URL パラメータ `:user_id` を取得して利用者 ID に変換する
 * - EditUserHeader（パンくずリスト）と EditUserForm / EditUserFormBankAccount（編集フォーム）を Suspense でラップする
 * - partner_feature_flag.bank_account が ON の場合は EditUserFormBankAccount を表示し、
 *   OFF の場合は既存の EditUserForm を表示する
 */
export const Screen: React.FC = () => {
  const { user_id = '' } = useParams<{ user_id: string }>();
  const userId = Number.parseInt(user_id, 10);

  if (Number.isNaN(userId)) {
    return <Navigate to={getUsersLink()} replace />;
  }

  return (
    <Suspense fallback={<Loader />}>
      <EditUserHeader userId={userId} />
      <main className={styles.body}>
        <EditUserContent userId={userId} />
      </main>
    </Suspense>
  );
};
